import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Search as SearchIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { UserForInvitation } from '../../../services/userSearchService'
import userSearchService from '../../../services/userSearchService'
import ProfileAvatar from '../../ProfileAvatar'

interface UserSearchAutocompleteProps {
  value: UserForInvitation | null
  onChange: (user: UserForInvitation | null) => void
  companyId?: string
  disabled?: boolean
  placeholder?: string
  label?: string
  error?: boolean
  helperText?: string
  isGlobalAdmin?: boolean
}

export const UserSearchAutocomplete: React.FC<UserSearchAutocompleteProps> = ({
  value,
  onChange,
  companyId,
  disabled = false,
  placeholder = "Search for existing users...",
  label = "User",
  error = false,
  helperText,
  isGlobalAdmin = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<UserForInvitation[]>([])
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced search function
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setOptions([])
      setSearchError(null)
      return
    }

    setLoading(true)
    setSearchError(null)

    try {
      // Cancel previous search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          console.log('UserSearchAutocomplete: Searching with params:', { query, companyId, limit: 20 })
          const result = await userSearchService.searchUsersForInvitation(
            query,
            companyId,
            20
          )
          
          console.log('UserSearchAutocomplete: Search result:', result)
          
          if (result.success) {
            setOptions(result.users)
          } else {
            setOptions([])
            setSearchError('Search failed')
          }
        } catch (error) {
          console.error('Search error:', error)
          setOptions([])
          setSearchError('Failed to search users')
        } finally {
          setLoading(false)
        }
      }, 300)
    } catch (error) {
      console.error('Search error:', error)
      setOptions([])
      setSearchError('Failed to search users')
      setLoading(false)
    }
  }, [companyId])

  // Handle input change
  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue)
    performSearch(newInputValue)
  }, [performSearch])

  // Handle option selection
  const handleOptionSelect = useCallback((event: React.SyntheticEvent, newValue: UserForInvitation | null) => {
    onChange(newValue)
    if (newValue) {
      setInputValue(userSearchService.formatDisplayName(newValue))
    }
  }, [onChange])

  // Clear selection
  const handleClear = useCallback(() => {
    onChange(null)
    setInputValue('')
    setOptions([])
    setSearchError(null)
  }, [onChange])

  // Get option label
  const getOptionLabel = useCallback((option: UserForInvitation | string) => {
    if (typeof option === 'string') {
      return option
    }
    return userSearchService.formatDisplayName(option)
  }, [])

  // Get unique option key
  const getOptionKey = useCallback((option: UserForInvitation) => {
    return option.id || option.email || `user-${Math.random()}`
  }, [])

  // Render option
  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: UserForInvitation) => {
    const status = userSearchService.getUserStatus(option)
    const canInvite = userSearchService.canInviteUser(option)
    const optionKey = getOptionKey(option)

    return (
      <ListItem 
        {...props} 
        key={optionKey}
        sx={{ 
          opacity: canInvite ? 1 : 0.6,
          cursor: canInvite ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        <ListItemAvatar>
          <ProfileAvatar
            userId={option.id}
            name={userSearchService.formatDisplayName(option)}
            photoUrl={option.profile?.customAvatarUrl}
            patternStyle={option.profile?.avatar}
            size={40}
          />
        </ListItemAvatar>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ListItemText
            primary={userSearchService.formatDisplayName(option)}
            secondary={
              <Box sx={{ display: 'block' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                    {option.email}
                  </Typography>
                </Box>
                {option.profile?.title && (
                  <Box sx={{ display: 'block', marginBottom: 0.5 }}>
                    <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                      {option.profile.title}
                    </Typography>
                  </Box>
                )}
                {!canInvite && (
                  <Box sx={{ display: 'block', marginTop: 0.5 }}>
                    <Chip 
                      label="Already a member" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
          />
        </Box>
        
        {/* Status icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {status.emailVerified && (
            <Tooltip title="Email verified">
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            </Tooltip>
          )}
          {!canInvite && (
            <Tooltip title="Already a member of this company">
              <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      </ListItem>
    )
  }, [])

  // Filter options (client-side filtering for better UX)
  const filterOptions = useCallback((options: UserForInvitation[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return options
    
    const query = inputValue.toLowerCase()
    return options.filter(option => {
      const displayName = userSearchService.formatDisplayName(option).toLowerCase()
      const email = option.email.toLowerCase()
      const username = option.username.toLowerCase()
      
      return displayName.includes(query) || 
             email.includes(query) || 
             username.includes(query)
    })
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        value={value}
        onChange={handleOptionSelect}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        filterOptions={filterOptions}
        loading={loading}
        disabled={disabled}
        clearOnBlur={false}
        selectOnFocus={false}
        handleHomeEndKeys={false}
        freeSolo={false}
        autoComplete={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error || !!searchError}
            helperText={searchError || helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                  {params.InputProps.startAdornment}
                </Box>
              ),
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {loading && <CircularProgress size={20} />}
                  {value && (
                    <Tooltip title="Clear selection">
                      <IconButton
                        size="small"
                        onClick={handleClear}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {params.InputProps.endAdornment}
                </Box>
              ),
            }}
          />
        )}
        noOptionsText={
          inputValue.length < 2 
            ? "Type at least 2 characters to search..." 
            : "No users found"
        }
        loadingText="Searching users..."
        sx={{
          '& .MuiAutocomplete-paper': {
            backgroundColor: 'rgba(16, 20, 31, 0.98)',
            border: '1px solid rgba(56, 154, 203, 0.2)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          },
          '& .MuiAutocomplete-option': {
            '&:hover': {
              backgroundColor: 'rgba(56, 154, 203, 0.1)',
            },
            '&[aria-selected="true"]': {
              backgroundColor: 'rgba(56, 154, 203, 0.2)',
            },
          },
        }}
      />
      
      {value && (
        <Box sx={{ mt: 1 }}>
          <Alert 
            severity={userSearchService.canInviteUser(value) ? "info" : "warning"}
            sx={{ 
              backgroundColor: 'rgba(56, 154, 203, 0.1)',
              border: '1px solid rgba(56, 154, 203, 0.2)',
            }}
          >
            <Typography variant="body2">
              {userSearchService.canInviteUser(value) 
                ? `Ready to invite ${userSearchService.formatDisplayName(value)} to the company`
                : `${userSearchService.formatDisplayName(value)} is already a member of this company`
              }
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  )
}
